import { useCallback, useContext, useMemo } from 'react';
import { Star } from 'react-feather';
import type { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import * as Table from '@/components/Table';
import {
  AdditionalFilters,
  ClipsTable,
  ContentCell,
  GoodQuoteHeader,
  KeywordSearch,
  SourceCell,
  SourceHeader,
  TagsCell,
  TagsHeader,
  ActionsMenu,
} from '@trinity/chat-components/components/ProjectClips';
import { ProjectClipsDataContext, ProjectClipsFiltersContext } from '@trinity/chat-components/contexts/project-clips';
import { SetTaggedMomentEditParamsContext } from '@trinity/chat-components/contexts/tagged-moment';
import type { ProjectClips } from '@trinity/chat/types';
import { Search } from '@/components/icons/Search';
import { HighlightsContainer } from './Highlights.Container';
import { TagsFilterMenu } from './TagsFilterMenu';
import styles from './style/Project.Highlights.module.css';

type Props = unknown;

export const ProjectHighlights = (_props: Props) => {
  return (
    <HighlightsContainer>
      <ProjectHighlightsContent />
    </HighlightsContainer>
  );
};

ProjectHighlights.displayName = 'Project.Highlights';

const ProjectHighlightsContent = () => {
  const suspended = useContext(ProjectClipsDataContext);
  const columns = useHighlightsColumns();

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <Table.Layout.Box>
          <Table.Layout.Header title="Highlights">
            <div className={styles.search}>
              <KeywordSearch SearchIconComponent={Search} />
              <AdditionalFilters />
            </div>
          </Table.Layout.Header>

          <ClipsTable
            columns={columns}
            data={suspended.data}
            empty={suspended.empty}
            loading={suspended.loading}
          />
        </Table.Layout.Box>
      </div>
    </div>
  );
};

type TableItem = ProjectClips.Item;

const useHighlightsColumns = (): ColumnDef<TableItem>[] => {
  return useMemo(
    () => [
      {
        id: 'source',
        header: SourceHeader,
        cell: SourceCell,
        meta: { className: styles.call },
      },
      {
        id: 'tags',
        header: HighlightsTagsHeader,
        cell: TagsCell,
        meta: { className: styles.tag },
      },
      {
        id: 'good-quote',
        header: GoodQuoteHeader,
        cell: GoodQuoteCell,
        meta: { className: styles.goodQuoteCell },
      },
      {
        id: 'snippet',
        header: 'Content',
        cell: ContentCell,
        meta: { className: styles.text },
      },
      {
        id: 'actions',
        header: '',
        cell: ActionsCell,
        meta: { className: styles.actions },
      },
    ],
    []
  );
};

/** Row action cell — no download clip for chat app */
const ActionsCell = ({ row }: CellContext<TableItem, unknown>) => (
  <ActionsMenu
    monologues={row.original.content}
    range={{ start: row.original.range.start, end: row.original.range.end }}
    transcriptId={row.original.transcript.id}
  />
);

/** Good Quote star cell — opens TaggedMoment modal with isGoodQuote: true */
const GoodQuoteCell = ({ row }: CellContext<TableItem, unknown>) => {
  const setEditParams = useContext(SetTaggedMomentEditParamsContext);
  const filters = useContext(ProjectClipsFiltersContext);

  const isGoodQuote = row.original.isGoodQuote;

  const className = [styles.star, isGoodQuote ? styles.active : ''].filter(Boolean).join(' ');

  const handleClick = useCallback(() => {
    if (isGoodQuote || !setEditParams) return;
    setEditParams({
      keyword: filters.keyword,
      isGoodQuote: true,
      transcriptId: row.original.transcript.id,
      range: row.original.range,
      tagId: null,
    });
  }, [isGoodQuote, setEditParams, filters.keyword, row.original.transcript.id, row.original.range]);

  return <Star onClick={handleClick} className={className} />;
};

/** TagsHeader wrapper that injects the tag filter menu */
const HighlightsTagsHeader = (props: HeaderContext<TableItem, unknown>) => (
  <TagsHeader {...props} filterMenu={(onClose) => <TagsFilterMenu onSubmit={onClose} />} />
);
